import { isDefined, isNil } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { CbhIcon, CollapsibleBlock, Text } from "@src/appV2/redesign/system/ui-components";

import { ButtonInternalLink } from "../../../components/ButtonInternalLink";
import { type Consequence, ConsequencePill } from "./ConsequencePill";
import { getIcon } from "./icon";
import { type Multiplier, MultiplierPill } from "./MultiplierPill";

interface ActivityItemProps {
  label: string;
  consequence: Consequence | undefined;
  caption?: string | React.ReactNode;
  expanded?: boolean;
  setExpanded?: () => void;
  multiplier?: Multiplier;
  children?: React.ReactNode;
  customIcon?: React.ReactNode;
  /**
   * @default ConsequencePill
   */
  consequenceElement?: React.ReactNode;
}

export function ActivityItem(props: ActivityItemProps) {
  const {
    label,
    consequence,
    multiplier,
    caption,
    expanded,
    setExpanded,
    children,
    customIcon,
    consequenceElement,
  } = props;

  const defaultIcon = isDefined(consequence) ? (
    <CbhIcon type={getIcon(consequence)} size="xSmall" />
  ) : null;
  const icon = customIcon ?? defaultIcon;

  const titleRow = (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={5}>
      <Stack direction="row" spacing={2} alignItems="center">
        {(isDefined(consequence) || isDefined(customIcon)) && icon}
        <Text
          bold
          sx={{
            paddingLeft: () => (isNil(consequence) ? 6 : 0),
            paddingY: () => (isNil(consequence) ? 2 : 0),
          }}
        >
          {label}
        </Text>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center">
        {isDefined(multiplier) &&
          isDefined(consequence) &&
          typeof consequence === "number" &&
          consequence !== 0 && <MultiplierPill multiplier={multiplier} />}
        {isDefined(consequence) &&
          (consequenceElement ?? <ConsequencePill consequence={consequence} />)}
      </Stack>
    </Stack>
  );

  const captionElement = isDefined(caption) && (
    <Text variant="body2" sx={{ paddingLeft: 6 }}>
      {caption}
    </Text>
  );

  const titleWithCaption = (
    <Stack direction="column">
      {titleRow}
      {captionElement}
    </Stack>
  );

  return (
    <Stack direction="column">
      {isDefined(children) && isDefined(expanded) && isDefined(setExpanded) ? (
        <CollapsibleBlock
          title={titleWithCaption}
          expanded={expanded}
          LinkComponent={ButtonInternalLink}
          onChange={setExpanded}
        >
          {children}
        </CollapsibleBlock>
      ) : (
        titleWithCaption
      )}
    </Stack>
  );
}
